/// <amd-module name="VSS/Features/Markdown/Plugins/LinkTransformer" />
import * as MarkdownIt from "markdown-it";

import { IState } from "VSS/Features/Markdown/MarkdownIt";
import { MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import * as Resources from "VSS/Features/Markdown/Resources";
import { isExternalUrl } from "VSS/Features/Markdown/Utils/UrlHelper";

export class LinkTransformer {
    private _options: MarkdownRendererOptions;

    constructor(md: MarkdownIt.MarkdownIt, options?: MarkdownRendererOptions) {
        this._options = options || {};

        // This rule always runs, which adjusts external links
        md.core.ruler.push("external_links", state => this._linkTokensTransformer(state));

        // If showing of external link icon is kept on, add the output rule to linkify rule
        if (!options || !options.hideExternalImageIcon) {
            md.renderer.rules["link_close"] = (token, idx) => this._externalLinkIcon(token, idx);
        }
    }

    private _externalLinkIcon = (tokens: MarkdownIt.Token[], linkCloseIndex: number): string => {
        let correspondingLinkOpenToken: MarkdownIt.Token | undefined = undefined;
        let hasImageInLink = false;
        for (let i = linkCloseIndex - 1; i >= 0; i--) {
            if (tokens[i].type === "image") {
                hasImageInLink = true;
                continue;
            }

            if (tokens[i].type === "link_open") {
                if (!hasImageInLink && linkCloseIndex - i > 1) {
                    // External link icons should be shown only if there is some token other than image
                    correspondingLinkOpenToken = tokens[i];
                }

                break;
            }
        }

        const href = !!correspondingLinkOpenToken ? correspondingLinkOpenToken.attrGet("href") : undefined;
        if (href && isExternalUrl(href)) {
            // Overriding regular link closing tag and amending the external icon do it
            return "</a> <span class='fabric-icon ms-Icon--NavigateExternalInline font-size' role='presentation' aria-hidden='true'> </span>";
        } else {
            return "</a>";
        }
    };

    private _linkTokensTransformer = (state: IState): void => {
        // Run transformer on all top level tokens
        state.tokens.map(this._applyTransformerToHierarchy);
    };

    private _applyTransformerToHierarchy = (token: MarkdownIt.Token, index: number, tokens: MarkdownIt.Token[]): void => {
        if (token.children) {
            // If token has children, run transform recursively on all of them
            token.children.map(this._applyTransformerToHierarchy, this);
        }

        // If token is a link opening tag, apply the transform
        if (token.type === "link_open") {
            let href = token.attrGet("href");

            // Assemble link text / alt text from images forward-looking in the array.

            let linkProcessing = true;
            let forwardLookingIndex = 1;
            let labelContent = "";

            /*
            * Token structure is as follows:
            * token (whole link with content)
            * - token type "link_open" (is the opening <a> tag that is created)
            * - possible token type "text" (contains the content of the link inside the [] markdown)
            * - possible token type "image" (contains the image and its content or the alt text) 
            * - token type "link_close" (is the closing </a> tag that is created)
            * 
            * This structure isn't definite, hence the while loop to get anything between "link_open" and "link_close"
            * 
            * This lookup is necessary to catch the text (as it is a separate token) to insert it into the aria-label.
            */

            while (linkProcessing) {
                let currentToken = tokens[index + forwardLookingIndex];

                if (!currentToken) break;

                if (currentToken.type !== "link_close") {
                    if (currentToken.type === "image" && currentToken.children !== null) {
                        // Alt text is contained (for some reason) in a child node.
                        // Also, checking to make sure it is not undefined (in markdown, it'd be like: 
                        //   ![](image.png)
                        // as "undefined" would then be inserted into the link, which could confuse the user.
                        currentToken.children[0]?.content !== undefined ? labelContent += ` ${currentToken.children[0]?.content}` : null;
                    } else if (currentToken.type === "text") {
                        labelContent += ` ${currentToken.content}`;
                    }
                    forwardLookingIndex++;
                } else {
                    linkProcessing = false;
                }
            }

            labelContent = labelContent.trim();

            if (href) {
                // If an additional link transformer is passed in options, add it to list of token transformers
                if (this._options.linkTransformer) {
                    href = this._options.linkTransformer(href);
                    if (href) {
                        token.attrSet("href", href);
                    }
                }

                if (this._options.linkCustomStyleAppender) {
                    const prevStyles = token.attrGet("class") || undefined;
                    const customClasses = this._options.linkCustomStyleAppender(href, prevStyles);
                    let transformedStyle: string = customClasses ? customClasses.join(" ") : "";

                    if (prevStyles) {
                        transformedStyle = transformedStyle.concat(" " + prevStyles);
                    }
                    token.attrSet("class", transformedStyle);
                }

                if (isExternalUrl(href!)) {
                    // Open external links in new window
                    token.attrSet("target", "_blank");
                    // Don't allow external links access to window.opener via javascript
                    // Noreferrer is needed for back-compat with IE&Edge, which don't support noopener
                    token.attrSet("rel", "noopener noreferrer");
                    // Add aria-label of the link text plus "(opens in new tab or window)"
                    const content = labelContent !== "" ? `${labelContent} ` : "";
                    token.attrSet("aria-label", `${content}${Resources.OpenInNewWindow}`);
                }
            }

            // linkCustomTitleProvider should be applied after all other transformations
            // as it requires attributes from other transformations
            if (this._options.linkCustomTitleProvider) {
                const customTitle = this._options.linkCustomTitleProvider(token);
                if (customTitle) {
                    token.attrSet("title", customTitle);
                }
            }
        }
    };
}
